import type { FC, ReactNode } from "react";
import { useMemo, useState } from "react";
import Button from "../../../components/staff/Button";
import Plus from "../../../components/Atomos/Icons/EditMode/Plus";
import Modal from "../../../components/Atomos/Modals/Modal";
import type { IPhoneNumber } from "../../../components/staff/PhoneNumber";
import type { FirmContactView } from "../../../interfaces";
import type { IOfficeNew } from "../../../interfaces/models/IOffice";
import ManageFirmContact from "./ManageFirmContact";
import {
  applyPrimaryContactFallbacks,
  mergeFirmContactsWithOffices,
  requiresCoreFirmContacts,
  resolveOfficesForFirmContactProfile,
  type FirmContactProfileOfficeOptions,
} from "./manageDataAdminOfficeRules";
import { formatProfessionalsOfficeFilterLabel } from "./professionalsOfficeFilterLabel";
import { firmContactBlockKey } from "../../../utils/firmContactUtils";
import { toReadableLabel } from "../../../utils/contactOptionLabel";
import { getJurisdictionLabel } from "../../../utils/jurisdiction/structuredJurisdiction";
import {
  FIRM_CONTACT_SLOTS_MANAGE_PROFESSIONALS,
  isFirmContactAssigned,
} from "../../../utils/firmContactRoleConfig";
import {
  ResponsiveProfileRecords,
  type ProfileRecordItem,
} from "./ResponsiveProfileRecords";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import ProfileOfficeTabs, {
  useProfileOfficeTabSelection,
} from "./ProfileOfficeTabs";

export const PERSON_ROLE_DATA_ADMIN = "dataAdmin" as const;
export const PERSON_ROLE_MEMBER = "member" as const;

export function isPersonRoleDataAdmin(role: unknown): boolean {
  return String(role ?? "") === PERSON_ROLE_DATA_ADMIN;
}

type ContactsData = FirmContactView["primaryContact"];

type FirmContactTableSlot =
  | {
      key: string;
      label: string;
      cardinality: "single";
      resolve: (view: FirmContactView) => ContactsData | undefined;
    }
  | {
      key: string;
      label: string;
      cardinality: "multi";
      resolve: (view: FirmContactView) => ContactsData[] | undefined;
    };

const FIRM_CONTACT_TABLE_SLOTS: FirmContactTableSlot[] =
  FIRM_CONTACT_SLOTS_MANAGE_PROFESSIONALS.map(
  (slot) =>
    slot.cardinality === "single"
      ? {
          key: slot.key,
          label: slot.title,
          cardinality: "single" as const,
          resolve: (view: FirmContactView) =>
            view[slot.field] as ContactsData | undefined,
        }
      : {
          key: slot.key,
          label: slot.title,
          cardinality: "multi" as const,
          resolve: (view: FirmContactView) => view[slot.field] ?? [],
        },
);

function formatPhoneDisplay(phone: IPhoneNumber | undefined): string {
  if (!phone) {
    return "";
  }
  const code = phone.code?.trim() ?? "";
  const num = phone.phone?.trim() ?? "";
  if (!code && !num) {
    return "";
  }
  return [code, num].filter(Boolean).join(" ");
}

function displayOrEmpty(value: string): ReactNode {
  const trimmed = value.trim();
  if (!trimmed) {
    return <span className="text-gray-300 italic">Not Assigned</span>;
  }
  return trimmed;
}

export interface FirmContactProfileProps {
  firmContactData: FirmContactView[];
  offices: IOfficeNew[];
  membershipType?: string;
  /** When true, include offices with showInDirectory=false (WSG internal). */
  includeOfficesHiddenFromDirectory?: boolean;
  onFirmContactChange?: (data: FirmContactView[]) => void;
}

function buildTableRowsForView(firmContact: FirmContactView): {
  rowKey: string;
  typeLabel: string;
  name: string;
  email: string;
  phone: string;
}[] {
  const rows: {
    rowKey: string;
    typeLabel: string;
    name: string;
    email: string;
    phone: string;
  }[] = [];

  for (const slot of FIRM_CONTACT_TABLE_SLOTS) {
    if (slot.cardinality === "single") {
      const contact = slot.resolve(firmContact);
      const assigned = isFirmContactAssigned(contact);
      rows.push({
        rowKey: `${firmContactBlockKey(firmContact)}-${slot.key}`,
        typeLabel: slot.label,
        name: assigned ? toReadableLabel(contact!.fullName) : "",
        email: assigned ? (contact!.email?.trim() ?? "") : "",
        phone: assigned ? formatPhoneDisplay(contact!.phone) : "",
      });
      continue;
    }

    const list = slot.resolve(firmContact) ?? [];
    const assignedList = list.filter(isFirmContactAssigned);
    if (assignedList.length === 0) {
      rows.push({
        rowKey: `${firmContactBlockKey(firmContact)}-${slot.key}-empty`,
        typeLabel: slot.label,
        name: "",
        email: "",
        phone: "",
      });
      continue;
    }

    assignedList.forEach((contact, index) => {
      rows.push({
        rowKey: `${firmContactBlockKey(firmContact)}-${slot.key}-${index}`,
        typeLabel: slot.label,
        name: toReadableLabel(contact.fullName),
        email: contact.email?.trim() ?? "",
        phone: formatPhoneDisplay(contact.phone),
      });
    });
  }

  return rows;
}

function toMobileContactRecords(
  tableRows: ReturnType<typeof buildTableRowsForView>,
): ProfileRecordItem[] {
  return tableRows.map((row) => ({
    key: row.rowKey,
    title: row.typeLabel,
    fields: [
      { label: "Name", value: displayOrEmpty(row.name) },
      { label: "Email", value: displayOrEmpty(row.email) },
      { label: "Phone", value: displayOrEmpty(row.phone) },
    ],
  }));
}

const FirmContactProfile: FC<FirmContactProfileProps> = ({
  firmContactData,
  offices,
  membershipType,
  includeOfficesHiddenFromDirectory = false,
  onFirmContactChange: _onFirmContactChange,
}) => {
  const [showEditDataAdminModal, setShowEditDataAdminModal] = useState(false);
  const { isStaffMode } = useStaffMode();

  const profileOfficeOptions = useMemo<FirmContactProfileOfficeOptions>(
    () => ({ includeOfficesHiddenFromDirectory }),
    [includeOfficesHiddenFromDirectory],
  );

  const profileOffices = useMemo(
    () => resolveOfficesForFirmContactProfile(offices, profileOfficeOptions),
    [offices, profileOfficeOptions],
  );

  const officeById = useMemo(
    () =>
      new Map(
        profileOffices.map((office) => [
          String(office.id ?? "").trim(),
          office,
        ]),
      ),
    [profileOffices],
  );

  const displayContacts = useMemo(
    () =>
      mergeFirmContactsWithOffices(
        firmContactData,
        offices,
        firmContactData[0]?.firmId ?? profileOffices[0]?.firm ?? "",
        firmContactData[0]?.firmName ?? profileOffices[0]?.firmName ?? "",
        membershipType,
        profileOfficeOptions,
      ).map((contact) =>
        applyPrimaryContactFallbacks(contact, {
          requireCoreContacts: requiresCoreFirmContacts(
            membershipType,
            String(contact.officeId ?? "").trim(),
            profileOffices,
          ),
        }),
      ),
    [firmContactData, offices, profileOffices, membershipType, profileOfficeOptions],
  );

  const officeTabs = useMemo(
    () =>
      displayContacts.map((firmContact) => {
        const office = officeById.get(
          String(firmContact.officeId ?? "").trim(),
        );
        return {
          id: firmContactBlockKey(firmContact),
          label: office
            ? formatProfessionalsOfficeFilterLabel(office)
            : getJurisdictionLabel({ jurisdiction: firmContact.jurisdiction }) || "Office",
        };
      }),
    [displayContacts, officeById],
  );

  const { activeTabId: activeContactKey, setActiveTabId: setActiveContactKey } =
    useProfileOfficeTabSelection(officeTabs);

  const activeFirmContact = useMemo(
    () =>
      displayContacts.find(
        (contact) => firmContactBlockKey(contact) === activeContactKey,
      ),
    [displayContacts, activeContactKey],
  );

  const activeTableRows = useMemo(
    () =>
      activeFirmContact ? buildTableRowsForView(activeFirmContact) : [],
    [activeFirmContact],
  );

  return (
    <div className="flex flex-col overflow-hidden rounded-md border border-gray-100">
      <div className="flex flex-col gap-4 px-4 py-2">
        <Modal
          isModalOpen={showEditDataAdminModal}
          toggleModal={() => setShowEditDataAdminModal(false)}
          title="Manage Firm Contacts"
          className="max-w-[1000px]"
          bodyClassName="min-h-0 overflow-visible"
        >
          <ManageFirmContact
            firmContactData={firmContactData}
            offices={offices}
            membershipType={membershipType}
            includeOfficesHiddenFromDirectory={
              includeOfficesHiddenFromDirectory
            }
            onClose={() => setShowEditDataAdminModal(false)}
            onSaved={(updatedData) => {
              _onFirmContactChange?.(updatedData);
              setShowEditDataAdminModal(false);
            }}
          />
        </Modal>
        <div className="flex items-center gap-2">
          <h3
            className={`w-full ${isStaffMode ? "text-secondary" : "text-primary"}`}
          >
            WSG Firm Contact Profile
          </h3>
          <div className="flex w-full items-end justify-end">
            <Button
              text="Edit Firm Contact(s)"
              color={isStaffMode ? "secondary" : "primary"}
              variant="filled"
              size="fixed"
              onClick={() => {
                setShowEditDataAdminModal(true);
              }}
              icon={<Plus />}
              labelNonVisible={["xxs", "xs"]}
            />
          </div>
        </div>
        <ProfileOfficeTabs
          tabs={officeTabs}
          activeTabId={activeContactKey}
          onSelect={setActiveContactKey}
          className="mb-1 -mx-4"
        />
        {activeFirmContact ? (
            <div className="gap-2 text-gray-font">
              <ResponsiveProfileRecords
                records={toMobileContactRecords(activeTableRows)}
              />
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="py-2 w-1/4 text-left font-semibold">
                        Type
                      </th>
                      <th className="py-2 w-1/4 text-left font-semibold">
                        Name
                      </th>
                      <th className="py-2 w-1/4 text-left font-semibold">
                        Email
                      </th>
                      <th className="py-2 w-1/4 text-left font-semibold">
                        Phone
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeTableRows.map((row) => (
                      <tr
                        key={row.rowKey}
                        className="border-b border-gray-50 last:border-0"
                      >
                        <td className="py-1 pr-4 align-top font-semibold">
                          {row.typeLabel}
                        </td>
                        <td className="py-1 pr-4 align-top">
                          {displayOrEmpty(row.name)}
                        </td>
                        <td className="py-1 pr-4 align-top break-all">
                          {displayOrEmpty(row.email)}
                        </td>
                        <td className="py-3 align-top">
                          {displayOrEmpty(row.phone)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
      </div>
    </div>
  );
};
export default FirmContactProfile;
